import { Injectable, inject } from '@angular/core';
import {
	HttpInterceptor,
	HttpRequest,
	HttpHandler,
	HttpEvent,
	HttpResponse,
	HttpInterceptorFn,
	HttpHandlerFn,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { RateLimitService } from '../../services/rate-limit/rate-limit.service';

// Functional interceptor for Angular 21 standalone approach
export const rateLimitInterceptor: HttpInterceptorFn = (
	req: HttpRequest<unknown>,
	next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
	const rateLimitService = inject(RateLimitService);

	return next(req).pipe(
		tap({
			next: (event) => {
				// Only process HTTP responses
				if (event instanceof HttpResponse) {
					const headers = event.headers;

					// Extract rate limit headers
					const rateLimitHeaders = {
						'x-ratelimit-limit': headers.get('x-ratelimit-limit'),
						'x-ratelimit-remaining': headers.get('x-ratelimit-remaining'),
						'x-ratelimit-reset': headers.get('x-ratelimit-reset'),
						'retry-after': headers.get('retry-after'),
					};

					// Extract endpoint from request URL
					const endpoint = extractEndpoint(req.url);

					// Update rate limit service if headers are present
					rateLimitService.updateFromHeaders(rateLimitHeaders, endpoint);
				}
			},
			error: (error) => {
				// Also handle rate limit headers in error responses (especially 429)
				if (error.headers) {
					const rateLimitHeaders = {
						'x-ratelimit-limit': error.headers.get('x-ratelimit-limit'),
						'x-ratelimit-remaining': error.headers.get('x-ratelimit-remaining'),
						'x-ratelimit-reset': error.headers.get('x-ratelimit-reset'),
						'retry-after': error.headers.get('retry-after'),
					};

					const endpoint = extractEndpoint(error.url || req.url);
					rateLimitService.updateFromHeaders(rateLimitHeaders, endpoint);
				}
			},
		}),
	);
};

/**
 * Extract the endpoint path from full URL
 * Example: "http://localhost:8081/api/v1/auth/login" -> "/api/v1/auth/login"
 */
function extractEndpoint(url: string): string {
	try {
		const urlObj = new URL(url);
		return urlObj.pathname;
	} catch {
		// If URL parsing fails, try to extract path from relative URL
		const match = url.match(/^https?:\/\/[^/]+(\/.*)/);
		return match ? match[1] : url;
	}
}

// Class-based interceptor for NgModule approach
@Injectable()
export class RateLimitInterceptorClass implements HttpInterceptor {
	constructor(private rateLimitService: RateLimitService) {}

	intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		return next.handle(req).pipe(
			tap({
				next: (event) => {
					// Only process HTTP responses
					if (event instanceof HttpResponse) {
						const headers = event.headers;

						// Extract rate limit headers
						const rateLimitHeaders = {
							'x-ratelimit-limit': headers.get('x-ratelimit-limit'),
							'x-ratelimit-remaining': headers.get('x-ratelimit-remaining'),
							'x-ratelimit-reset': headers.get('x-ratelimit-reset'),
							'retry-after': headers.get('retry-after'),
						};

						// Extract endpoint from request URL
						const endpoint = this.extractEndpoint(req.url);

						// Update rate limit service if headers are present
						this.rateLimitService.updateFromHeaders(rateLimitHeaders, endpoint);
					}
				},
				error: (error) => {
					// Also handle rate limit headers in error responses (especially 429)
					if (error.headers) {
						const rateLimitHeaders = {
							'x-ratelimit-limit': error.headers.get('x-ratelimit-limit'),
							'x-ratelimit-remaining': error.headers.get('x-ratelimit-remaining'),
							'x-ratelimit-reset': error.headers.get('x-ratelimit-reset'),
							'retry-after': error.headers.get('retry-after'),
						};

						const endpoint = this.extractEndpoint(error.url || req.url);
						this.rateLimitService.updateFromHeaders(rateLimitHeaders, endpoint);
					}
				},
			}),
		);
	}

	private extractEndpoint(url: string): string {
		try {
			const urlObj = new URL(url);
			return urlObj.pathname;
		} catch {
			// If URL parsing fails, try to extract path from relative URL
			const match = url.match(/^https?:\/\/[^/]+(\/.*)/);
			return match ? match[1] : url;
		}
	}
}
